<template>
  <PmAppointmentsContainerPure
    v-if="propsAppointmentContainer"
    v-slot="{ packingContainer, constrainedStartDate, constrainedEndDate }"
    class="PmStockTypeService"
    v-bind="propsAppointmentContainer"
    :container-start-date="containerStartDate"
    :container-end-date="containerEndDate"
    :day-width="store.state.cssVar.variables.dayWidth.value"
    :content-can-be-hidden="props.contentCanBeHidden"
    :packed-element="props.packedElement"
    :update-group-visible="false"
    :items-for-content-bounds="
      artificialAppointments.itemsForContentBounds.value
    "
    :is-highlighted="isHighlighted"
    @show-details="showDetails"
    @jump="jumpToStockTypeServiceInSidebar"
  >
    <PmPackingElementPure
      v-for="appointment in artificialAppointments.appointments.value"
      :id="`appointment-${appointment.id}`"
      :key="appointment.id"
      v-slot="slotProps"
      :packing-container="packingContainer"
    >
      <PmAppointmentPure
        :container-start-date="constrainedStartDate"
        :container-end-date="constrainedEndDate"
        v-bind="appointment.props"
        :packed-element="slotProps.packedElement"
        :day-width="store.state.cssVar.variables.dayWidth.value"
      >
        <PmResource
          v-for="resource in appointment.resources"
          :id="resource.id"
          :key="resource.id"
          :edit-buttons-visible="persoplanState.state.value.matches('edit')"
          :container-start-date="containerStartDate"
        />
      </PmAppointmentPure>
    </PmPackingElementPure>
  </PmAppointmentsContainerPure>

  <div v-else class="PmStockTypeService">
    <!-- We need a empty div for packing in any case -->
  </div>
</template>

<script setup lang="ts">
/**
 * @note Don't use v-if on PmAppointmentsContainer, it might break packing!
 */
import { computed } from 'vue'
import { useStore } from 'vuex'

import { EVENT } from '@/constants/events'
import { CALENDAR_ITEM_TYPE, DATA_MODAL_TYPE } from '@/constants/persoplan'

import { parseServerDateString } from '@/utilities/date'

import { persoplanStateKey, injectStrict } from '@/utilities/inject'
import { useCachedQuery } from '@/composition/useCachedQuery'
import { useArtificialAppointments } from '@/composition/useArtificialAppointments'
import EventHub from '@/eventHub'

import type { PackingItem } from '@/functional/packing'

import PmAppointmentsContainerPure, {
  type Props as PropsAppointmentContainerPure,
} from '@/components/persoplan/PmAppointmentsContainer/PmAppointmentsContainerPure.vue'
import PmPackingElementPure from '@/components/persoplan/PmPackingContainer/PmPackingElementPure.vue'
import PmAppointmentPure from '@/components/persoplan/PmAppointment/PmAppointmentPure.vue'
import PmResource from '@/components/persoplan/PmResource/PmResource.vue'

import { StockTypeServiceDocument } from '../../../../generated/graphql'

export interface Props {
  containerStartDate: Date
  containerEndDate: Date
  id: number
  contentCanBeHidden?: boolean
  packedElement?: PackingItem
}

const props = withDefaults(defineProps<Props>(), {})

const store = useStore()
const persoplanState = injectStrict(persoplanStateKey)

const startDate = computed(() => {
  return parseServerDateString(stockTypeService.value?.startDate)
})

const endDate = computed(() => {
  return parseServerDateString(stockTypeService.value?.endDate)
})

const stockTypeServiceQuery = useCachedQuery(StockTypeServiceDocument, () => {
  const queryVariables = store.getters['queryVariables/calendar']

  return {
    ...queryVariables,
    id: props.id,
  }
})

const stockTypeService = computed(
  () => stockTypeServiceQuery.result.value?.stockTypeService
)

const isHighlighted = computed({
  get: function () {
    return store.getters['persoplan/isItemHighlighted']({
      id: props.id,
      type: CALENDAR_ITEM_TYPE.STOCK_TYPE_SERVICE,
    })
  },
  set: function (value) {
    if (value === true) {
      store.commit('persoplan/addToHighlightedItems', {
        id: props.id,
        type: CALENDAR_ITEM_TYPE.STOCK_TYPE_SERVICE,
      })
    } else {
      store.commit('persoplan/removeFromHighlightedItems', {
        id: props.id,
        type: CALENDAR_ITEM_TYPE.STOCK_TYPE_SERVICE,
      })
    }
  },
})

const artificialAppointments = useArtificialAppointments({
  resources: () => stockTypeService.value?.resources,
  containerStartDate: startDate,
  containerEndDate: endDate,
})

const propsAppointmentContainer = computed(() => {
  if (!stockTypeService.value) return
  if (!startDate.value) throw new Error('startDate is undefined')
  if (!endDate.value) throw new Error('endDate is undefined')

  const result: PropsAppointmentContainerPure = {
    id: props.id,
    startDate: startDate.value,
    endDate: endDate.value,
    type: CALENDAR_ITEM_TYPE.STOCK_TYPE_SERVICE,
    title: stockTypeService.value?.caption ?? undefined,
    number: stockTypeService.value?.number ?? undefined,
  }

  return result
})

function showDetails() {
  EventHub.$emit(EVENT.DATA_MODAL_SHOW, {
    type: DATA_MODAL_TYPE.STOCK_TYPE_SERVICE,
    id: props.id,
  })
}

function jumpToStockTypeServiceInSidebar() {
  EventHub.$emit(EVENT.SIDEBAR_STOCK_TYPE_SERVICE_SCROLL_INTO_VIEW, {
    id: props.id,
    type: CALENDAR_ITEM_TYPE.STOCK_TYPE_SERVICE,
  })

  store.commit('persoplan/clearHighlightedItems')
  isHighlighted.value = true
}
</script>

<style lang="scss">
.PmStockTypeService {
  $block: &;
}
</style>
